import React, { useEffect, useMemo, useState } from 'react';
import { SortBy, buildComparator } from '../../components/SortBy';
import { Link, useNavigate } from 'react-router-dom';
import {
  getBills, getBillStats, getGmailStatus, getGmailLoginUrl,
  disconnectGmail, dismissBill, pollBillsNow, learnFromHistory,
  getXeroStatus, deleteBill, pushAllGreenBills,
  getBillsSettings, updateBillsSettings, sendBillsDigestNow,
  getUnattachedBills, reattachBillToXero,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import ScanReceiptDialog from '../../components/ScanReceiptDialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs';
import {
  Mail, RefreshCw, Link2, Unlink, ExternalLink, AlertTriangle,
  CheckCircle2, Trash2, Inbox, CircleSlash, Sparkles,
  ShieldCheck, ShieldAlert, ShieldX, Rocket, Zap, Camera,
  Paperclip, Send,
} from 'lucide-react';
import { toast } from 'sonner';
const fmtMoney = (v, ccy = 'GBP') => {
  if (v === null || v === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy }).format(v);
  } catch {
    return `£${Number(v || 0).toFixed(2)}`;
  }
};

import { formatDate } from '../../utils/date';

const fmtDate = (iso) => (iso ? formatDate(iso) : '');

const Bills = () => {
  const [gmail, setGmail] = useState({ connected: false, configured: false, email: '' });
  const [xeroConnected, setXeroConnected] = useState(false);
  const [stats, setStats] = useState({ parsed: 0, pushed: 0, failed: 0, dismissed: 0, total: 0 });
  const [bills, setBills] = useState([]);
  const [tab, setTab] = useState('parsed');
  const [polling, setPolling] = useState(false);
  const [training, setTraining] = useState(false);
  const [pushingAll, setPushingAll] = useState(false);
  const [autoPush, setAutoPush] = useState(false);
  const [autoPushSaving, setAutoPushSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [unattached, setUnattached] = useState([]);
  const [reattachBusyId, setReattachBusyId] = useState(null);

  const refresh = async () => {
    try {
      const [s, b] = await Promise.all([getBillStats(), getBills()]);
      setStats(s);
      setBills(b);
    } catch (e) { /* silent */ }
    // Cleanup widget — runs in background, never blocks the page.
    try {
      const u = await getUnattachedBills();
      setUnattached(u || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    getGmailStatus().then(setGmail).catch(() => {});
    getXeroStatus().then(s => setXeroConnected(!!s?.connected)).catch(() => {});
    getBillsSettings().then(s => setAutoPush(!!s?.autoPushReady)).catch(() => {});
    refresh();

    // After OAuth callback, surface ?gmail_connected or ?gmail_error
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_connected')) {
      toast.success('Gmail connected. Polling will start within 5 minutes.');
      window.history.replaceState({}, '', window.location.pathname);
      getGmailStatus().then(setGmail).catch(() => {});
    } else if (params.get('gmail_error')) {
      toast.error(`Gmail connection failed: ${params.get('gmail_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connectGmail = async () => {
    try {
      const { authUrl } = await getGmailLoginUrl();
      window.location.href = authUrl;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Gmail login');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Gmail? Polling will stop and stored tokens will be cleared.')) return;
    try {
      await disconnectGmail();
      toast.success('Gmail disconnected');
      setGmail({ ...gmail, connected: false, email: '' });
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const res = await pollBillsNow();
      if (res.skipped) {
        toast.error(res.reason || 'Skipped');
      } else {
        toast.success(
          `Scanned ${res.scanned} email${res.scanned === 1 ? '' : 's'} · ${res.newBills} new bill${res.newBills === 1 ? '' : 's'} · ${res.autoPushed} pushed`,
        );
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Polling failed');
    } finally {
      setPolling(false);
    }
  };

  const trainNow = async () => {
    if (!window.confirm('Re-scan every previously-pushed bill and teach the suggestion engine? This is safe to run any time.')) return;
    setTraining(true);
    try {
      const res = await learnFromHistory();
      toast.success(`Trained on ${res.billsScanned} bill(s) · ${res.memoryEntriesWritten} line memory entries`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Training failed');
    } finally {
      setTraining(false);
    }
  };

  const pushAllGreen = async () => {
    const n = stats.readyToPush || 0;
    if (!n) {
      toast.error('No bills are fully verified and account-coded yet');
      return;
    }
    if (!window.confirm(`Push ${n} fully-verified bill${n === 1 ? '' : 's'} to Xero as DRAFT? Only bills where every line matches the original PDF and has an account code will be sent.`)) return;
    setPushingAll(true);
    try {
      const res = await pushAllGreenBills();
      const ok = res.pushedCount || 0;
      const bad = res.failedCount || 0;
      if (ok && !bad) {
        toast.success(`Pushed ${ok} bill${ok === 1 ? '' : 's'} to Xero`);
      } else if (ok && bad) {
        toast.warning(`Pushed ${ok} · Failed ${bad} — check the Failed tab`);
      } else if (bad) {
        toast.error(`All ${bad} push${bad === 1 ? '' : 'es'} failed — check the Failed tab`);
      } else {
        toast.message('Nothing to push');
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk push failed');
    } finally {
      setPushingAll(false);
    }
  };

  const handleAutoPushToggle = async (next) => {
    // Optimistic toggle so the switch feels instant; revert on failure.
    const prev = autoPush;
    setAutoPush(next);
    setAutoPushSaving(true);
    try {
      const saved = await updateBillsSettings({ autoPushReady: next });
      setAutoPush(!!saved?.autoPushReady);
      toast.success(next
        ? 'Auto-push enabled — new fully-verified bills will go straight to Xero'
        : 'Auto-push disabled — bills will queue for review',
      );
    } catch (e) {
      setAutoPush(prev);
      toast.error(e?.response?.data?.detail || 'Could not save the setting');
    } finally {
      setAutoPushSaving(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await sendBillsDigestNow();
      if (res?.sent) {
        toast.success(`Digest emailed to ${res.recipient} · ${res.pushedCount} pushed · ${res.failedCount} failed`);
      } else if (res?.reason === 'no-recipient-configured') {
        toast.error('No recipient — set BILLS_DIGEST_RECIPIENT or ADMIN_CC_EMAIL in backend .env');
      } else if (res?.reason === 'resend-not-configured') {
        toast.error('Resend is not configured — set RESEND_API_KEY in backend .env');
      } else {
        toast.message(`Digest not sent (${res?.reason || 'unknown reason'})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const filtered = useMemo(() => bills.filter(b => b.status === tab), [bills, tab]);

  // User's saved sort preference for the bills inbox.
  const [sort, setSort] = useState({ key: 'received', dir: 'desc' });
  const sortedBills = useMemo(() => {
    const getter = (b, key) => {
      switch (key) {
        case 'supplier': return (b.supplierName || '').toLowerCase();
        case 'amount': return Number(b.totalAmount ?? b.amount) || 0;
        case 'invoiceNumber': return String(b.invoiceNumber || '');
        case 'issue': return b.issueDate ? new Date(b.issueDate).getTime() : 0;
        case 'due': return b.dueDate ? new Date(b.dueDate).getTime() : 0;
        case 'received':
        default: return b.receivedAt ? new Date(b.receivedAt).getTime()
                       : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      }
    };
    return [...filtered].sort(buildComparator(sort, getter));
  }, [filtered, sort]);

  const navigate = useNavigate();

  const handleOpen = (b) => {
    navigate(`/admin/bills/${b.id}`);
  };

  const handleDismiss = async (b) => {
    if (!window.confirm(`Dismiss "${b.emailSubject || b.id}"? It won't be processed again.`)) return;
    setBusyId(b.id);
    try {
      await dismissBill(b.id);
      toast.success('Dismissed');
      await refresh();
    } catch (e) {
      toast.error('Failed to dismiss');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete this bill from the queue? The email source remains in Gmail.`)) return;
    setBusyId(b.id);
    try {
      await deleteBill(b.id);
      toast.success('Deleted');
      await refresh();
    } catch (e) {
      toast.error('Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="bills-page">
      <PageHeader
        title="Bills Inbox"
        description="AI parses incoming supplier invoices, receipts and payment confirmations from your Gmail and creates draft bills in Xero automatically."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => setScanOpen(true)}
              data-testid="scan-receipt-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              title="Take a photo or upload an image of a paper receipt — parsed by AI just like email bills"
            >
              <Camera className="h-4 w-4 mr-2" />
              Scan receipt
            </Button>
            {(stats.readyToPush || 0) > 0 && xeroConnected && (
              <Button
                size="sm"
                onClick={pushAllGreen}
                disabled={pushingAll}
                data-testid="push-all-green-btn"
                className="bg-green-600 hover:bg-green-700 text-white"
                title="Push every fully-verified, account-coded bill to Xero in one go"
              >
                <Rocket className={`h-4 w-4 mr-2 ${pushingAll ? 'animate-pulse' : ''}`} />
                {pushingAll ? 'Pushing…' : `Push all green (${stats.readyToPush})`}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={trainNow}
              disabled={training}
              data-testid="train-history-btn"
              title="Re-scan all pushed bills and teach the suggestion engine"
            >
              <Sparkles className={`h-4 w-4 mr-2 ${training ? 'animate-pulse' : ''}`} />
              {training ? 'Training...' : 'Train from history'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={pollNow}
              disabled={polling || !gmail.connected}
              data-testid="poll-now-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${polling ? 'animate-spin' : ''}`} />
              {polling ? 'Scanning...' : 'Scan now'}
            </Button>
          </div>
        }
      />

      {/* Connection bar */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ConnectionCard
          icon={Mail}
          title="Gmail"
          subtitle={
            !gmail.configured ? 'Not configured (set GOOGLE_CLIENT_ID in .env)' :
            gmail.connected ? gmail.email || 'Connected' : 'Not connected'
          }
          ok={gmail.connected}
          actionLabel={gmail.connected ? 'Disconnect' : 'Connect Gmail'}
          actionIcon={gmail.connected ? Unlink : Link2}
          onAction={gmail.connected ? handleDisconnect : connectGmail}
          actionDisabled={!gmail.configured}
          actionDestructive={gmail.connected}
          testId="gmail-connect-btn"
        />
        <ConnectionCard
          icon={Sparkles}
          title="Xero"
          subtitle={
            xeroConnected
              ? 'Connected — bills can be pushed as DRAFT supplier bills (ACCPAY)'
              : 'Not connected — bills will queue here until you connect Xero'
          }
          ok={xeroConnected}
          actionLabel={xeroConnected ? 'Manage Xero' : 'Connect Xero'}
          actionIcon={ExternalLink}
          onAction={() => { window.location.href = '/admin/xero'; }}
          testId="manage-xero-link"
        />
      </div>

      {/* Unattached pushed bills — cleanup widget. Surfaces every bill
          that's in Xero but missing its source PDF/image, with a one-
          click reattach button. Only renders when there's something to
          do, so the inbox stays clean for users with no backlog. */}
      {unattached.length > 0 && (
        <div
          className="bg-white border border-amber-200 rounded-md overflow-hidden"
          data-testid="unattached-bills-widget"
        >
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-amber-700" />
            <div className="flex-1 min-w-0">
              <div className="font-display uppercase tracking-wide text-sm text-amber-900">
                Pushed bills missing their Xero attachment
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                {unattached.length} bill{unattached.length === 1 ? '' : 's'} in Xero without the source PDF or receipt image attached. Use Re-attach to upload the file directly to the existing Xero bill.
              </div>
            </div>
          </div>
          <ul className="divide-y divide-slate-100 max-h-72 overflow-auto">
            {unattached.map((b) => (
              <li
                key={b.id}
                className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50"
                data-testid={`unattached-row-${b.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <Link
                      to={`/admin/bills/${b.id}`}
                      className="font-mono text-slate-900 hover:underline truncate"
                    >
                      {b.xeroInvoiceNumber || b.id}
                    </Link>
                    <span className="text-slate-500 truncate">— {b.supplierName || 'Unknown supplier'}</span>
                    {b.source === 'receipt' && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                        <Camera className="h-2.5 w-2.5" /> Receipt
                      </span>
                    )}
                  </div>
                  {b.xeroAttachment?.error && (
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={b.xeroAttachment.error}>
                      {b.xeroAttachment.error}
                    </div>
                  )}
                  {!b.canReattach && (
                    <div className="text-[11px] text-rose-700 mt-0.5">
                      Source file is permanently lost — tap "Scan receipt" to upload a fresh photo.
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!b.canReattach && !b.canReupload}
                  data-testid={`reattach-btn-${b.id}`}
                  onClick={async () => {
                    // If the source file is permanently lost, jump to the
                    // Bill Detail page with ?reupload=1 so the file picker
                    // pops automatically and the admin can scan a fresh
                    // photo (works great on iPhone Safari).
                    if (!b.canReattach && b.canReupload) {
                      navigate(`/admin/bills/${b.id}?reupload=1`);
                      return;
                    }
                    setReattachBusyId(b.id);
                    try {
                      const res = await reattachBillToXero(b.id);
                      toast.success(`Attached ${res.filename} to Xero`);
                      setUnattached((rows) => rows.filter((r) => r.id !== b.id));
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Re-attach failed');
                    } finally {
                      setReattachBusyId(null);
                    }
                  }}
                >
                  {b.canReattach ? (
                    <>
                      <Send className={`h-3 w-3 mr-1 ${reattachBusyId === b.id ? 'animate-pulse' : ''}`} />
                      {reattachBusyId === b.id ? 'Sending…' : 'Re-attach'}
                    </>
                  ) : (
                    <>
                      <Camera className="h-3 w-3 mr-1" />
                      Scan receipt
                    </>
                  )}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Auto-push toggle */}
      <div
        className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row"
        data-testid="auto-push-toggle-row"
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`h-10 w-10 rounded flex items-center justify-center shrink-0 ${autoPush ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
            <Zap className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display uppercase tracking-wide text-sm text-slate-700">
              Auto-push when ready
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              When a newly-parsed bill comes back from AI verification fully matched and every line has an account code suggested, push it straight to Xero as DRAFT — no manual review needed. You'll still see it in the "Pushed" tab.
            </div>
            {/* Today's auto-push activity pills + manual digest test */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-green-50 text-green-700 border border-green-200"
                data-testid="auto-pushed-today-pill"
                title="Bills auto-pushed to Xero since 00:00 UTC"
              >
                <Rocket className="h-3 w-3" />
                Auto-pushed today: {stats.autoPushedToday || 0}
              </span>
              {(stats.autoFailedToday || 0) > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-700 border border-red-200"
                  data-testid="auto-failed-today-pill"
                  title="Auto-push attempts that errored — open the Failed tab"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Failed: {stats.autoFailedToday}
                </span>
              )}
              <button
                type="button"
                onClick={sendDigestNow}
                disabled={sendingDigest}
                className="text-[11px] text-slate-500 hover:text-slate-900 underline underline-offset-2 disabled:opacity-50"
                data-testid="send-digest-now-btn"
                title="Send the daily digest email right now (ignores the 06:00 UTC window)"
              >
                {sendingDigest ? 'Sending…' : 'Send test digest'}
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
          <span className={`text-xs font-medium ${autoPush ? 'text-violet-700' : 'text-slate-400'}`}>
            {autoPush ? 'On' : 'Off'}
          </span>
          <Switch
            checked={autoPush}
            onCheckedChange={handleAutoPushToggle}
            disabled={autoPushSaving || !xeroConnected}
            data-testid="auto-push-toggle"
            aria-label="Auto-push when ready"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Awaiting push" value={stats.parsed} tone="amber" testId="stat-parsed" />
        <StatCard label="Pushed to Xero" value={stats.pushed} tone="green" testId="stat-pushed" />
        <StatCard label="Failed" value={stats.failed} tone="red" testId="stat-failed" />
        <StatCard label="Dismissed" value={stats.dismissed} tone="slate" testId="stat-dismissed" />
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="bills-tabs">
        <div className="-mx-1 overflow-x-auto pb-1 pl-1 pr-1 md:overflow-visible">
          <TabsList>
            <TabsTrigger value="parsed" data-testid="tab-parsed">
              <Inbox className="h-4 w-4 mr-2" /> Awaiting push ({stats.parsed})
            </TabsTrigger>
            <TabsTrigger value="pushed" data-testid="tab-pushed">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Pushed ({stats.pushed})
            </TabsTrigger>
            <TabsTrigger value="failed" data-testid="tab-failed">
              <AlertTriangle className="h-4 w-4 mr-2" /> Failed ({stats.failed})
            </TabsTrigger>
            <TabsTrigger value="dismissed" data-testid="tab-dismissed">
              <CircleSlash className="h-4 w-4 mr-2" /> Dismissed ({stats.dismissed})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={tab} className="mt-4">
          {filtered.length === 0 ? (
            <EmptyState
              connected={gmail.connected}
              tab={tab}
              onConnect={connectGmail}
              onPoll={pollNow}
            />
          ) : (
            <div className="space-y-3">
              <SortBy
                storageKey="sort.bills"
                defaultValue="received:desc"
                onChange={setSort}
                testId="bills-sort"
                options={[
                  { value: 'received:desc', label: 'Received — newest' },
                  { value: 'received:asc',  label: 'Received — oldest' },
                  { value: 'supplier:asc',  label: 'Supplier (A–Z)' },
                  { value: 'supplier:desc', label: 'Supplier (Z–A)' },
                  { value: 'amount:desc', label: 'Amount — high to low' },
                  { value: 'amount:asc',  label: 'Amount — low to high' },
                  { value: 'issue:desc', label: 'Invoice date — newest' },
                  { value: 'issue:asc',  label: 'Invoice date — oldest' },
                  { value: 'due:asc', label: 'Due date — soonest' },
                  { value: 'due:desc', label: 'Due date — latest' },
                  { value: 'invoiceNumber:desc', label: 'Invoice # (high to low)' },
                  { value: 'invoiceNumber:asc',  label: 'Invoice # (low to high)' },
                ]}
              />
              <BillsTable
                bills={sortedBills}
                busyId={busyId}
                tab={tab}
                onOpen={handleOpen}
                onDismiss={handleDismiss}
                onDelete={handleDelete}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ScanReceiptDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onParsed={(bill) => {
          refresh();
          navigate(`/admin/bills/${bill.id}`);
        }}
      />
    </div>
  );
};

const ConnectionCard = ({
  icon: Icon, title, subtitle, ok, actionLabel, actionIcon: ActionIcon,
  onAction, actionDisabled, actionDestructive, testId,
}) => (
  <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 flex items-center justify-between gap-3">
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <div className={`h-10 w-10 rounded flex items-center justify-center shrink-0 ${ok ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display uppercase tracking-wide text-sm text-slate-700">{title}</div>
        <div className="text-xs text-slate-500 truncate">{subtitle}</div>
      </div>
    </div>
    <Button
      variant={actionDestructive ? 'outline' : 'default'}
      size="sm"
      onClick={onAction}
      disabled={actionDisabled}
      className={`shrink-0 ${actionDestructive ? 'text-red-600 hover:text-red-700' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
      data-testid={testId}
    >
      <ActionIcon className="h-4 w-4 sm:mr-2" />
      <span className="hidden sm:inline">{actionLabel}</span>
    </Button>
  </div>
);

const StatCard = ({ label, value, tone, testId }) => {
  const tones = {
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    green: 'bg-green-50 text-green-800 border-green-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`border rounded-md p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="text-3xl font-display tracking-tight">{value}</div>
      <div className="text-[11px] uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
};

const EmptyState = ({ connected, tab, onConnect, onPoll }) => (
  <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
    <Mail className="h-10 w-10 mx-auto text-slate-300 mb-3" />
    {!connected ? (
      <>
        <div className="font-display uppercase tracking-wide text-slate-700 mb-1">Connect your Gmail</div>
        <div className="text-sm text-slate-500 mb-4">Once connected, we'll scan for invoices and receipts every 5 minutes and create draft bills in Xero automatically.</div>
        <Button onClick={onConnect} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="empty-connect-gmail">
          <Link2 className="h-4 w-4 mr-2" /> Connect Gmail
        </Button>
      </>
    ) : (
      <>
        <div className="font-display uppercase tracking-wide text-slate-700 mb-1">Nothing here yet</div>
        <div className="text-sm text-slate-500 mb-4">
          {tab === 'parsed' ? 'No bills waiting to be pushed.' :
           tab === 'pushed' ? 'No bills pushed to Xero yet.' :
           tab === 'failed' ? 'No failures — nice.' :
           'Nothing dismissed.'}
        </div>
        {tab === 'parsed' && (
          <Button variant="outline" size="sm" onClick={onPoll}>
            <RefreshCw className="h-4 w-4 mr-2" /> Scan inbox now
          </Button>
        )}
      </>
    )}
  </div>
);

const BillsTable = ({ bills, busyId, tab, onOpen, onDismiss, onDelete }) => (
  <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
    {/* Desktop table */}
    <div className="hidden lg:block overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-4 py-3">Supplier</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Invoice #</th>
            <th className="text-left px-4 py-3">Date</th>
            <th className="text-right px-4 py-3">Total</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {bills.map(b => (
            <tr
              key={b.id}
              data-testid={`bill-row-${b.id}`}
              className="hover:bg-slate-50 cursor-pointer"
              onClick={() => onOpen(b)}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {b.source === 'receipt' && (
                    <Camera className="h-3.5 w-3.5 text-amber-700 shrink-0" aria-label="Receipt scan" />
                  )}
                  <div className="font-medium text-slate-900 truncate flex-1">
                    {b.supplierName || <span className="text-slate-400 italic">Unknown</span>}
                  </div>
                  <VerificationBadge summary={b.verificationSummary} />
                </div>
                {b.confidence !== undefined && (
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
                    Confidence {Math.round((b.confidence || 0) * 100)}%
                  </div>
                )}
              </td>
              <td className="px-4 py-3 max-w-xs">
                <div className="text-slate-700 truncate" title={b.emailSubject || b.receiptFilename}>
                  {b.source === 'receipt'
                    ? (b.receiptFilename || 'Scanned receipt')
                    : (b.emailSubject || '(no subject)')}
                </div>
                <div className="text-xs text-slate-400 truncate" title={b.emailFrom}>
                  {b.source === 'receipt'
                    ? <span className="uppercase tracking-wider text-amber-700">Receipt · {b.paymentMethod || 'on-account'}</span>
                    : b.emailFrom}
                </div>
              </td>
              <td className="px-4 py-3 text-slate-700 font-mono text-xs">{b.invoiceNumber || '—'}</td>
              <td className="px-4 py-3 text-slate-600">{fmtDate(b.issueDate)}</td>
              <td className="px-4 py-3 text-right font-medium text-slate-900">{fmtMoney(b.total, b.currency)}</td>
              <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1">
                  {(tab === 'parsed' || tab === 'failed') && (
                    <Button
                      size="sm"
                      onClick={() => onOpen(b)}
                      className="bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid={`review-bill-${b.id}`}
                    >
                      Review &amp; push
                    </Button>
                  )}
                  {tab === 'pushed' && b.xeroDeepLink && (
                    <a
                      href={b.xeroDeepLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-slate-200 hover:border-slate-400"
                      data-testid={`xero-link-${b.id}`}
                    >
                      <ExternalLink className="h-3 w-3" /> View in Xero
                    </a>
                  )}
                  {tab !== 'dismissed' && tab !== 'pushed' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDismiss(b)}
                      disabled={busyId === b.id}
                      className="text-slate-500 hover:text-slate-700"
                      title="Dismiss — never process again"
                      data-testid={`dismiss-bill-${b.id}`}
                    >
                      <CircleSlash className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(b)}
                    disabled={busyId === b.id}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    title="Delete from queue"
                    data-testid={`delete-bill-${b.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {b.lastError && tab === 'failed' && (
                  <div className="text-[11px] text-red-600 mt-2 max-w-xs truncate" title={b.lastError}>
                    {b.lastError}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Mobile cards */}
    <div className="lg:hidden divide-y divide-slate-100">
      {bills.map(b => (
        <button
          key={b.id}
          type="button"
          onClick={() => onOpen(b)}
          data-testid={`bill-card-${b.id}`}
          className="w-full text-left p-4 hover:bg-slate-50 active:bg-slate-100 transition-colors"
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-medium text-slate-900 truncate flex-1 flex items-center gap-2">
              {b.source === 'receipt' && (
                <Camera className="h-3.5 w-3.5 text-amber-700 shrink-0" aria-label="Receipt scan" />
              )}
              <span className="truncate">
                {b.supplierName || <span className="text-slate-400 italic">Unknown supplier</span>}
              </span>
              <VerificationBadge summary={b.verificationSummary} />
            </div>
            <div className="font-display text-base text-slate-900 tabular-nums shrink-0">
              {fmtMoney(b.total, b.currency)}
            </div>
          </div>
          <div className="text-xs text-slate-500 truncate" title={b.emailSubject || b.receiptFilename}>
            {b.source === 'receipt'
              ? (<span className="uppercase tracking-wider text-amber-700">Receipt · {b.paymentMethod || 'on-account'}</span>)
              : (b.emailSubject || '(no subject)')}
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
            <span className="font-mono">{b.invoiceNumber || '—'}</span>
            <span>{fmtDate(b.issueDate)}</span>
          </div>
          {b.lastError && tab === 'failed' && (
            <div className="text-[11px] text-red-600 mt-2 truncate" title={b.lastError}>
              {b.lastError}
            </div>
          )}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
            {(tab === 'parsed' || tab === 'failed') && (
              <span className="text-xs text-slate-700 font-medium inline-flex items-center gap-1">
                Tap to review &amp; push <ExternalLink className="h-3 w-3" />
              </span>
            )}
            {tab === 'pushed' && b.xeroDeepLink && (
              <a
                href={b.xeroDeepLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded border border-slate-200 hover:border-slate-400"
              >
                <ExternalLink className="h-3 w-3" /> View in Xero
              </a>
            )}
            {tab === 'dismissed' && <span className="text-xs text-slate-400">Dismissed</span>}
            <div className="flex items-center gap-1 ml-auto">
              {tab !== 'dismissed' && tab !== 'pushed' && (
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDismiss(b); }}
                  disabled={busyId === b.id} className="h-7 w-7 p-0 text-slate-500">
                  <CircleSlash className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(b); }}
                disabled={busyId === b.id} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </button>
      ))}
    </div>
  </div>
);

export default Bills;

// Small inbox-row badge that summarises every line's AI verification.
// Green shield = all lines match the original PDF.
// Amber = at least one partial.
// Red = at least one mismatch.
// Nothing rendered if verification hasn't run yet.
const VerificationBadge = ({ summary }) => {
  if (!summary || !summary.total) return null;
  const { matched = 0, partial = 0, mismatched = 0, errors = 0, total = 0 } = summary;
  let Icon = ShieldCheck;
  let cls = 'text-green-700 bg-green-50 border-green-200';
  let label = `All ${total} lines verified`;
  if (mismatched > 0) {
    Icon = ShieldX;
    cls = 'text-red-700 bg-red-50 border-red-200';
    label = `${mismatched}/${total} line${mismatched === 1 ? '' : 's'} mismatch the invoice`;
  } else if (partial > 0) {
    Icon = ShieldAlert;
    cls = 'text-amber-700 bg-amber-50 border-amber-200';
    label = `${partial}/${total} line${partial === 1 ? '' : 's'} need review`;
  } else if (errors === total) {
    return null;  // verification couldn't run for any line — don't pretend
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium shrink-0 ${cls}`}
      title={label}
    >
      <Icon className="h-3 w-3" />
      <span className="hidden md:inline">{matched}/{total}</span>
    </span>
  );
};
